import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { catchError, throwError, tap, finalize } from 'rxjs'
import { MessageService } from '@openng/optimus-ui/api'
import { inject, Injectable, signal, computed } from '@angular/core'
import { ErrorResponse } from 'api'
import { Router } from '@angular/router'

// Global error state management
@Injectable({ providedIn: 'root' })
export class GlobalErrorState {
	private readonly router = inject(Router)
	private readonly messageService = inject(MessageService)

	private readonly currentError = signal<ErrorDetails | null>(null)
	private readonly errorHistory = signal<ErrorDetails[]>([])
	private readonly loadingStates = signal<Map<string, LoadingState>>(new Map())

	readonly currentError$ = this.currentError.asReadonly()
	readonly errorHistory$ = this.errorHistory.asReadonly()
	readonly hasError = computed(() => this.currentError() !== null)
	readonly isLoading = computed(() => this.loadingStates().size > 0)

	constructor() {
		this.setupGlobalErrorHandling()
	}

	private setupGlobalErrorHandling(): void {
		// SSR guard: `window` is not available on the server. These global error
		// listeners are only meaningful in the browser anyway.
		if (typeof window === 'undefined') return

		// Handle uncaught promise rejections
		window.addEventListener('unhandledrejection', event => {
			this.handleError({
				message: event.reason?.message || 'Unhandled Promise Rejection',
				code: 'UNHANDLED_REJECTION',
				stack: event.reason?.stack,
				timestamp: new Date().toISOString(),
				userAction: 'Unknown',
			})
			event.preventDefault()
		})

		// Handle uncaught errors
		window.addEventListener('error', event => {
			this.handleError({
				message: event.message,
				code: 'UNCAUGHT_ERROR',
				stack: event.error?.stack,
				timestamp: new Date().toISOString(),
				userAction: 'Unknown',
			})
		})
	}

	handleError(error: ErrorDetails | Error | string, userAction = 'Unknown'): void {
		let errorDetails: ErrorDetails

		if (typeof error === 'string') {
			errorDetails = {
				message: error,
				timestamp: new Date().toISOString(),
				path: this.router.url,
				userAction,
			}
		} else if (error instanceof Error) {
			errorDetails = {
				message: error.message,
				code: 'JAVASCRIPT_ERROR',
				stack: error.stack,
				timestamp: new Date().toISOString(),
				path: this.router.url,
				userAction,
			}
		} else {
			errorDetails = {
				...error,
				timestamp: new Date().toISOString(),
				path: this.router.url,
				userAction,
			}
		}

		// Add to history
		this.errorHistory.update(history => [...history.slice(-9), errorDetails])

		// Set current error
		this.currentError.set(errorDetails)

		// Show user-friendly message
		this.showErrorMessage(errorDetails)

		// Log to console for debugging
		console.error('Error handled by GlobalErrorState:', errorDetails)

		// TODO: Send to error tracking service (Sentry, etc.)
		this.sendToErrorTracking(errorDetails)
	}

	setCurrentError(error: ErrorDetails): void {
		this.currentError.set(error)
		this.errorHistory.update(history => [...history.slice(-9), error])
	}

	clearError(): void {
		this.currentError.set(null)
	}

	setLoading(key: string, isLoading: boolean, message?: string, progress?: number): void {
		const states = this.loadingStates()
		const newStates = new Map(states)

		if (isLoading) {
			newStates.set(key, { isLoading, message, progress })
		} else {
			newStates.delete(key)
		}

		this.loadingStates.set(newStates)
	}

	getLoadingState(key: string): LoadingState | undefined {
		return this.loadingStates().get(key)
	}

	clearAllLoadingStates(): void {
		this.loadingStates.set(new Map())
	}

	private showErrorMessage(errorDetails: ErrorDetails): void {
		const severity = this.getErrorSeverity(errorDetails)
		const summary = this.getErrorSummary(errorDetails)
		const detail = errorDetails.message

		this.messageService.add({
			severity,
			summary,
			detail,
			life: 5000,
			closable: true,
		})
	}

	private getErrorSeverity(errorDetails: ErrorDetails): 'success' | 'info' | 'warn' | 'error' {
		if (errorDetails.code?.includes('NETWORK') || errorDetails.code?.includes('TIMEOUT')) {
			return 'warn'
		}
		if (errorDetails.code?.includes('AUTH') || errorDetails.code?.includes('PERMISSION')) {
			return 'warn'
		}
		return 'error'
	}

	private getErrorSummary(errorDetails: ErrorDetails): string {
		if (errorDetails.code?.includes('NETWORK')) {
			return 'Network Error'
		}
		if (errorDetails.code?.includes('AUTH')) {
			return 'Authentication Error'
		}
		if (errorDetails.code?.includes('PERMISSION')) {
			return 'Permission Denied'
		}
		if (errorDetails.code?.includes('VALIDATION')) {
			return 'Validation Error'
		}
		return 'Error Occurred'
	}

	private sendToErrorTracking(errorDetails: ErrorDetails): void {
		// TODO: Implement error tracking service integration
		// Example: Sentry.captureException(errorDetails)
		console.log('Error tracking:', errorDetails)
	}
}

export interface ErrorDetails {
	message: string
	code?: string
	stack?: string
	timestamp: string
	path?: string
	userAction?: string
}

export interface LoadingState {
	isLoading: boolean
	message?: string
	progress?: number
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
	const messageService = inject(MessageService)
	const router = inject(Router)
	const globalErrorState = inject(GlobalErrorState)

	// Generate a unique request key for loading state tracking
	const requestKey = `${req.method}-${req.url}-${Date.now()}`

	// Set loading state for this request
	globalErrorState.setLoading(requestKey, true, `Loading ${req.method} ${req.url}...`)

	return next(req).pipe(
		tap({
			next: () => {
				// Clear loading state on successful response
				globalErrorState.setLoading(requestKey, false)
			},
			error: error => {
				// Log error details for debugging
				console.error('HTTP Error:', {
					url: req.url,
					method: req.method,
					status: error.status,
					message: error.message,
					timestamp: new Date().toISOString(),
				})

				// Set global error state
				const errorDetails: ErrorDetails = {
					message: error.message || 'HTTP Error',
					code: `HTTP_${error.status}`,
					timestamp: new Date().toISOString(),
					path: req.url,
					userAction: `${req.method} ${req.url}`,
				}
				globalErrorState.setCurrentError(errorDetails)
			},
		}),
		catchError((error: HttpErrorResponse) => {
			let errorMessage = 'An unexpected error occurred'
			let errorSeverity: 'success' | 'info' | 'warn' | 'error' = 'error'
			let errorSummary = 'Error Occurred'

			// Handle different error types
			if (error.error instanceof ErrorEvent) {
				// Client-side error
				errorMessage = error.error.message
				errorSummary = 'Client Error'
				errorSeverity = 'error'
			} else if (error instanceof HttpErrorResponse) {
				// Server-side error
				handleHttpError(error, messageService, router)
				return throwError(() => error)
			}

			// Show user-friendly message
			messageService.add({
				severity: errorSeverity,
				summary: errorSummary,
				detail: errorMessage,
				life: 5000,
				closable: true,
				key: 'tc',
			})

			return throwError(() => error)
		}),
		finalize(() => {
			// Ensure loading state is cleared
			globalErrorState.setLoading(requestKey, false)
		})
	)
}

function handleHttpError(error: HttpErrorResponse, messageService: MessageService, router: Router): void {
	switch (error.status) {
		case 400: // Bad Request
			handleBadRequest(error, messageService)
			break

		case 401: // Unauthorized
			handleUnauthorizedError(messageService, router)
			break

		case 403: // Forbidden
			handleForbiddenError(messageService)
			break

		case 404: // Not Found
			handleNotFoundError(error, messageService)
			break

		case 406: // Not Acceptable
			handleNotAcceptableError(error, messageService)
			break

		case 422: // Unprocessable Entity
			handleValidationError(error, messageService)
			break

		case 429: // Too Many Requests
			handleRateLimitError(messageService)
			break

		case 500: // Internal Server Error
			handleServerError(messageService)
			break

		case 502: // Bad Gateway
		case 503: // Service Unavailable
		case 504: // Gateway Timeout
			handleServiceUnavailableError(messageService)
			break

		default:
			handleUnknownError(error, messageService)
			break
	}
}

function handleBadRequest(error: HttpErrorResponse, messageService: MessageService): void {
	const err = error.error as ErrorResponse
	const message = err.message || 'درخواست نامعتبر است'
	const details = err.details || []

	messageService.add({
		severity: 'warn',
		summary: 'خطای درخواست',
		detail: details.length > 0 ? `${message}: ${details.join(', ')}` : message,
		life: 6000,
		closable: true,
		key: 'tc',
	})
}

function handleUnauthorizedError(messageService: MessageService, router: Router): void {
	messageService.add({
		severity: 'warn',
		summary: 'خطای احراز هویت',
		detail: 'لطفاً مجدداً وارد حساب کاربری خود شوید',
		life: 4000,
		closable: true,
		key: 'tc',
	})

	// Redirect to login page after a short delay
	setTimeout(() => {
		router.navigate(['/login'])
	}, 2000)
}

function handleForbiddenError(messageService: MessageService): void {
	messageService.add({
		severity: 'warn',
		summary: 'دسترسی غیرمجاز',
		detail: 'شما مجوز دسترسی به این بخش را ندارید',
		life: 5000,
		closable: true,
		key: 'tc',
	})
}

function handleNotFoundError(error: HttpErrorResponse, messageService: MessageService): void {
	const err = error.error as ErrorResponse
	const message = err.message || 'منبع درخواستی یافت نشد'

	messageService.add({
		severity: 'warn',
		summary: 'یافت نشد',
		detail: message,
		life: 4000,
		closable: true,
		key: 'tc',
	})
}

function handleNotAcceptableError(error: HttpErrorResponse, messageService: MessageService): void {
	const err = error.error as ErrorResponse
	messageService.add({
		detail: err.message || 'درخواست قابل قبول نیست',
		severity: 'error',
		key: 'tc',
		life: 5000,
		closable: true,
	})
}

function handleValidationError(error: HttpErrorResponse, messageService: MessageService): void {
	const err = error.error as ErrorResponse
	const message = err.message || 'خطای اعتبارسنجی'
	const details = err.details || []

	messageService.add({
		severity: 'warn',
		summary: 'خطای اعتبارسنجی',
		detail: details.length > 0 ? `${message}: ${details.join(', ')}` : message,
		life: 6000,
		closable: true,
		key: 'tc',
	})
}

function handleRateLimitError(messageService: MessageService): void {
	messageService.add({
		severity: 'warn',
		summary: 'محدودیت درخواست',
		detail: 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً چند لحظه صبر کنید',
		life: 5000,
		closable: true,
		key: 'tc',
	})
}

function handleServerError(messageService: MessageService): void {
	messageService.add({
		severity: 'error',
		summary: 'خطای سرور',
		detail: 'خطای نامشخصی از سمت سرور رخ داده است. لطفاً چند لحظه دیگر تلاش کنید',
		life: 5000,
		closable: true,
		key: 'server-error',
	})
}

function handleServiceUnavailableError(messageService: MessageService): void {
	messageService.add({
		severity: 'warn',
		summary: 'سرویس در دسترس نیست',
		detail: 'سرویس مورد نظر موقتاً در دسترس نیست. لطفاً چند لحظه دیگر تلاش کنید',
		life: 5000,
		closable: true,
		key: 'tc',
	})
}

function handleUnknownError(error: HttpErrorResponse, messageService: MessageService): void {
	const err = error.error as ErrorResponse
	const message = err.message || error.message || 'خطای نامشخص'

	messageService.add({
		severity: 'error',
		summary: 'خطای نامشخص',
		detail: message,
		life: 5000,
		closable: true,
		key: 'tc',
	})
}
